<template>
  <div id="container" class="container-fluid">
    <!-- ============================================================== -->
    <!-- Bread crumb and right sidebar toggle -->
    <!-- ============================================================== -->
    <div class="row page-titles position-relative plan-delete-dim-target">
      <div class="col-md-5 col-8 align-self-center">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link to="/home">Home</router-link>
          </li>
          <li class="breadcrumb-item active">ケアプラン</li>
        </ol>
      </div>
    </div>
    <div class="row">
      <div class="col-md-3 position-relative plan-delete-dim-target">
        <div class="card">
          <div class="card-body">
            <div class="mb-3" :class="{ 'is-invalid': errors?.has('display_name') }">
              <select-office v-model="selectedOfficeId"></select-office>
            </div>
            <div v-if="selectedOfficeId !== null">
              <user-list :office-id="selectedOfficeIdNumber" v-model="selectedUserId" @change="selectedUser" />
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-9">
        <div class="card">
          <div class="card-body">
            <div id="calendar" ref="calendar" @mousemove="mousemove($event)">
              <div class="tooltipevent" v-if="active" :style="tipPlace">
                {{ tipTitle }}<br>
                {{ tipCaretime }}<br>
                {{ tipStaffs }}<br>
                {{ instructions }}
              </div>
            </div>
          </div>
        </div>
        <div class="plan-delete-dim-target position-relative mt-2">
          <p class="text-danger mb-0">※週番号をクリックすることでプランをコピーすることができます。</p>
        </div>
      </div>
    </div>
    <month-picker-modal v-model="showMonthPickerModal" :putmonth="month"
      @update:modelValue="val => showMonthPickerModal = val" @onMonth="monthJump" @cancel="reloadStart" />
    <copy-modal v-model="showCopyPlanModal" :office-id="Number(selectedOfficeId) || 0"
      :user-id="Number(selectedUserId) || 0" :week-start="selectedTargetDate || dayjs()"
      @update:modelValue="val => showCopyPlanModal = val" @change="onCopy" @cancel="reloadStart" />
    <plan-modal v-model="showPlanModal" :plan="selectedPlan || {}" @update:modelValue="val => showPlanModal = val"
      @edit="handleEdit" @change="onCopy" @cancel="reloadStart" />
    <plan-day-modal v-model="showPlanDayModal" :office-id="Number(selectedOfficeId) || 0"
      :user-id="Number(selectedUserId) || 0" :target-day="selectedTargetDate"
      @update:modelValue="val => showPlanDayModal = val" @change="onCopy" @cancel="reloadStart" @edit="handleEdit" />
    <plan-edit-modal v-if="showPlanEditModal" v-model="showPlanEditModal" :plan="selectedPlan"
      :office-id="selectedOfficeIdNumber" :staffs="staffs || []" :users="users || []" :params="params"
      @update:modelValue="val => showPlanEditModal = val" @close="onPlanEditModalClose"
      @cancel="onPlanEditModalCancel" />
    <reload-time-modal v-model="showReloadTimeModal" :reload="reloadTime"
      @update:modelValue="val => showReloadTimeModal = val" @timeSet="reloadSet" @cancel="reloadStart" />
    <div id='delete-mode'></div>
  </div>
</template>

<script setup>
/* global $ */
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import dayjs from 'dayjs'
import api from '../../libs/Api'
import _ from 'underscore'
import utils from '../../libs/Utils'
import Constants from '../../constants'
import SelectOffice from '../../components/SelectOffice'
import UserList from '../../components/UserList'
import CopyModal from '../../components/CopyModal'
import PlanModal from '../../components/PlanModal'
import PlanDayModal from '../../components/PlanDayModal'
import PlanEditModal from '../../components/PlanEditModal'
import MonthPickerModal from '@/components/MonthPickerModal'
import ReloadTimeModal from '@/components/ReloadTimeModal'
import repos from '../../libs/Repository'
import isoWeek from 'dayjs/plugin/isoWeek'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import { useToast } from 'bootstrap-vue-next'
import { useExecutor } from '@/libs/Executor'

// Extend dayjs with plugins
dayjs.extend(isoWeek)
dayjs.extend(weekOfYear)

const store = useStore()
const calendar = ref(null)
const toast = useToast()
const { exec } = useExecutor()

// Modal states
const showMonthPickerModal = ref(false)
const showCopyPlanModal = ref(false)
const showPlanModal = ref(false)
const showPlanDayModal = ref(false)
const showPlanEditModal = ref(false)
const showReloadTimeModal = ref(false)
const params = ref({})

// State
const errors = ref({
  has: (field) => false
})
const selectedPlan = ref({
  id: null,
  service: {
    start_day: '',
    start_hhmm: null,
    end_hhmm: null,
    service_type_id: null,
    service_code_id: null,
    service_unique_code_id: null,
    service_teresa_unique_code_id: null,
    destination: '',
    specific_office_addition_cd: null,
    special_addition_cd: null
  },
  staff: [],
  instruction: '',
  user: null,
  service_items: []
})
const isSakura = ref(false)
const startDate = ref(null)
const active = ref(false)
const tipTitle = ref('')
const month = ref('')
const tipCaretime = ref('')
const tipStaffs = ref('')
const instructions = ref('')
const tipPlace = ref({
  top: '0px',
  left: '0px'
})
const reloadTimer = ref(null)
const reloadTime = ref(null)
const selected = ref([])
const deleteMode = ref(false)
const isProcessing = ref(false)

// Computed
const selectedOfficeId = computed({
  get: () => store.state.careplan.targetOfficeId,
  set: (newVal) => {
    store.dispatch('careplan/setTargetOfficeId', newVal)
    selectedUserId.value = null
  }
})

const selectedUserId = computed({
  get: () => store.state.careplan.targetUserId,
  set: (newVal) => store.dispatch('careplan/setTargetUserId', newVal)
})

const selectedTargetDate = computed({
  get: () => store.state.careplan.targetDate,
  set: (newVal) => store.dispatch('careplan/setTargetDate', newVal)
})

const profile = computed(() => store.state.session.profile)
const staffs = computed(() => store.getters['careplan/staffs'] || [])
const users = computed(() => store.getters['careplan/users'] || [])

const hasTarget = computed(() => {
  const officeId = selectedOfficeId.value
  const userId = selectedUserId.value
  return Boolean(officeId) && Boolean(userId)
})

const selectedOfficeIdNumber = computed(() => {
  const id = selectedOfficeId.value
  return id ? Number(id) : 0
})

// Methods
const onPlanEditModalClose = async (updated) => {
  if (updated) {
    const currentDate = $(calendar.value).fullCalendar('getDate')
    const target = dayjs(currentDate)
    const from = target.clone().add(-1, 'months').startOf('month').format('YYYYMMDD')
    const to = target.clone().add(1, 'months').endOf('month').format('YYYYMMDD')

    try {
      isProcessing.value = true
      const res = await store.dispatch('careplan/getPlans', {
        mode: Constants.QUERY_MODE_ALL,
        office_id: selectedOfficeId.value,
        user_id: selectedUserId.value,
        start_day_from: from,
        start_day_to: to
      })

      const events = _.map(res, plan => {
        let title = null
        if (plan.service.service_type_id === Number(Constants.Code.ServiceType.UNIQUE_CODE)) {
          if (plan.service.service_unique_code_id) {
            title = plan.service.service_unique_code.display_name
          }
          else if (plan.service.service_teresa_unique_code_id) {
            title = plan.service.service_teresa_unique_code.display_name
          } else {
            title = plan.service.service_type.display_name
          }
        } else {
          title = plan.service.service_code_id ? plan.service.service_code.display_name : plan.service.service_type.display_name
        }

        return {
          id: plan.id,
          title: title,
          start: utils.formatDay(plan.service.start_day) + 'T' + utils.formatHHMM(plan.service.start_hhmm) + ':00',
          end: utils.formatDay(plan.service.start_day) + 'T' + utils.formatHHMM(plan.service.start_hhmm) + ':01',
          caretime: utils.formatHHMM(plan.service.start_hhmm) + ' ～ ' + utils.formatHHMM(plan.service.end_hhmm),
          staffs: getStaffs(plan),
          instructions: getInstructions(plan),
          tag: plan,
          color: utils.getEventColor(plan),
          allDay: false
        }
      })

      $(calendar.value).fullCalendar('removeEvents')
      $(calendar.value).fullCalendar('addEventSource', events)
    } catch (error) {
      console.error('Error refreshing calendar data:', error)
    } finally {
      isProcessing.value = false
    }
  }
  showPlanEditModal.value = false
}

const onPlanEditModalCancel = () => {
  showPlanEditModal.value = false
}

const eventRender = (event, element) => {
  return true
}

const initCalendar = () => {
  $(calendar.value).fullCalendar({
    locale: 'ja',
    eventClick: (event, jsEvent, view) => {
      planClick(event)
    },
    dayClick: dayClick,
    viewRender: viewRender,
    defaultDate: selectedTargetDate.value || dayjs(),
    weekNumbers: true,
    navLinks: true,
    firstDay: 1,
    events: [],
    header: {
      left: 'selectDeleteButton, cancelDeleteButton, title',
      right: 'prev,eventListButton,next, reloadTimeButtons, selectButton, updateButton, cancelSelectButton'
    },
    customButtons: {
      selectDeleteButton: {
        text: '選択削除',
        click: async function (e) {
          if (isProcessing.value) return
          if (!deleteMode.value) {
            e.target.innerText = '削除(0)'
            e.target.style.color = '#ffffff'
            e.target.style.background = '#dc3545'

            document.getElementsByClassName('fc-cancelDeleteButton-button')[0].style.display = 'block'
            document.getElementById('container').classList.toggle('delete-mode-overlay')

            deleteMode.value = true
          } else {
            if (selected.value.length === 0) return

            // Show confirmation modal
            if (selected.value.length === 0) return

            // Delete selected plans
            if (isProcessing.value) return
            isProcessing.value = true
            let ids = selected.value.map(item => item.id)

            exec(async () => {
              await api.deletePlans(ids)
              // Show success toast
              cancelSelect()
            })
              .catch((error) => {
                console.error('Error deleting plans:', error)
                toast.show({
                  title: 'エラー',
                  body: 'プランの削除中にエラーが発生しました。',
                  variant: 'danger',
                  delay: 2000
                })
              })
              .finally(() => {
                isProcessing.value = false
              })
          }
        }
      },
      cancelDeleteButton: {
        text: 'キャンセル',
        click: () => cancelSelect()
      },
      eventListButton: {
        text: '年月',
        click: () => {
          if (deleteMode.value) return
          planmonth()
        }
      },
      reloadTimeButtons: {
        text: '更新時間',
        click: () => {
          if (deleteMode.value) return
          reloadModal()
        }
      },
      prev: {
        text: '＜',
        click: () => {
          if (deleteMode.value) return
          // eslint-disable-next-line no-undef
          $(calendar.value).fullCalendar('prev')
        }
      },
      next: {
        text: '＞',
        click: () => {
          if (deleteMode.value) return
          // eslint-disable-next-line no-undef
          $(calendar.value).fullCalendar('next')
        }
      }
    },
    timeZone: 'Asia/Tokyo',
    timeFormat: 'HH:mm',
    eventRender: eventRender,
    navLinkDayClick: navLinkDayClick,
    navLinkWeekClick: navLinkWeekClick,
    eventMouseover: (event, jsEvent) => {
      tipTitle.value = 'サービス：' + event.title
      tipCaretime.value = '時間：' + event.caretime
      tipStaffs.value = '担当者：' + event.staffs
      instructions.value = '指示事項：' + event.instructions
      active.value = true
    },
    eventMouseout: (event, jsEvent) => {
      active.value = false
    }
  })

  if (!isSakura.value) {
    document.getElementsByClassName('fc-selectDeleteButton-button')[0].style.display = 'none'
  }
  document.getElementsByClassName('fc-cancelDeleteButton-button')[0].style.display = 'none'
  startDate.value = null
  load(selectedTargetDate.value)
}

const planmonth = () => {
  reloadStop()
  showMonthPickerModal.value = true
}

const reloadModal = () => {
  reloadStop()
  showReloadTimeModal.value = true
}

const mousemove = (e) => {
  tipPlace.value.top = (e.clientY - e.offsetY) - 180 + 'px'
  tipPlace.value.left = (e.clientX - e.offsetX * 2.5) - 500 + 'px'
}

const planClick = (event) => {
  if (deleteMode.value) {
    // Handle delete mode click
    const index = selected.value.findIndex(item => item.id === event.id)
    if (index === -1) {
      selected.value.push(event)
      event.className = 'selected'
    } else {
      selected.value.splice(index, 1)
      event.className = ''
    }
    document.getElementsByClassName('fc-selectDeleteButton-button')[0].innerText = `削除(${selected.value.length})`
    $(calendar.value).fullCalendar('updateEvent', event)
    return
  }

  // Click on day number - show PlanDayModal
  if (event.isDay) {
    selectedTargetDate.value = event.start
    showPlanDayModal.value = true
    return
  }

  // Sử dụng event.tag thay vì event
  selectedPlan.value = event.tag || event
  params.value = {
    start_day: event.start_day,
    end_day: event.end_day,
    user_id: selectedUserId.value
  }
  showPlanModal.value = true
}

const dayClick = async (date, jsEvent, view) => {
  if (deleteMode.value) return
  if (!hasTarget.value) return

  // Click on empty cell - show PlanEditModal
  selectedTargetDate.value = date

  // Reset store data when creating new plan
  await store.dispatch('careplan/setPlanToEdit', null)

  selectedPlan.value = null
  const day = date.format('YYYYMMDD')
  reloadStop()
  params.value = {
    start_day: day,
    end_day: day,
    user_id: selectedUserId.value
  }

  // Đảm bảo selectedPlan đã được gán giá trị trước khi mở modal
  nextTick(() => {
    showPlanEditModal.value = true
  })
}

const onCopy = (dst, copySourceWeek) => {
  startDate.value = null
  // コピー後は「コピー元の週」で再取得し、カレンダーもその日付に合わせる（表示していた範囲を維持）
  const loadTarget = copySourceWeek != null ? dayjs(copySourceWeek) : selectedTargetDate.value
  const targetDayjs = loadTarget ? (loadTarget instanceof dayjs ? loadTarget : dayjs(loadTarget)) : dayjs()
  load(targetDayjs)
  $(calendar.value).fullCalendar('gotoDate', targetDayjs.toDate())
  reloadStart()
}

const cancelSelect = () => {
  selected.value = []
  deleteMode.value = false
  updateSelectButtonText('選択削除')
  document.getElementsByClassName('fc-cancelDeleteButton-button')[0].style.display = 'none'
  let deleteBtn = document.getElementsByClassName('fc-selectDeleteButton-button')[0]
  deleteBtn.style.color = '#99abb4'
  deleteBtn.style.background = '#ffffff'
  deleteBtn.style.border = '1px solid transparent'
  document.getElementsByClassName('fc-selectDeleteButton-button')[0].classList.toggle('btn-select-delete')
  document.getElementById('container').classList.toggle('delete-mode-overlay')
  reload()
}

const updateSelectButtonText = (text) => {
  let buttons = document.getElementsByClassName('fc-selectDeleteButton-button')
  for (let i = 0; i < buttons.length; i++) {
    buttons[i].innerText = text
  }
}

const selectedUser = (event) => {
  startDate.value = null
  load(selectedTargetDate.value)
}

const reloadSet = (value) => {
  reloadTime.value = value
  repos.setAutoReload(value)

  reloadStart()
}

const reload = () => {
  startDate.value = null
  load(selectedTargetDate.value)
}

const reloadStart = () => {
  if (reloadTime.value === null) {
    clearInterval(reloadTimer.value)
  } else {
    clearInterval(reloadTimer.value)
    reloadTimer.value = setInterval(reload, reloadTime.value)
  }
}

const reloadStop = () => {
  clearInterval(reloadTimer.value)
}

const load = (target) => {
  exec(async () => {
    const currentDate = $(calendar.value).fullCalendar('getDate')
    month.value = dayjs(currentDate).format('YYYY-MM')

    if (!selectedUserId.value || !selectedOfficeId.value) return
    // 文字列・Date・store 経由の値も正しく dayjs に変換する（未指定時のみ今日）
    target = target ? (target instanceof dayjs ? target : dayjs(target)) : dayjs()
    const from = target.clone().add(-1, 'months').startOf('month').format('YYYYMMDD')
    if (startDate.value === from) return
    startDate.value = from
    selectedTargetDate.value = target
    const to = target.clone().add(1, 'months').endOf('month').format('YYYYMMDD')

    isProcessing.value = true
    store.dispatch('careplan/getPlans', {
      mode: Constants.QUERY_MODE_ALL,
      office_id: selectedOfficeId.value,
      user_id: selectedUserId.value,
      start_day_from: from,
      start_day_to: to
    })
      .then(res => {
        const events = _.map(res, plan => {
          let title = null
          if (plan.service.service_type_id === Number(Constants.Code.ServiceType.UNIQUE_CODE)) {
            if (plan.service.service_unique_code_id && plan.service.service_unique_code) {
              title = plan.service.service_unique_code.display_name
            }
            else if (plan.service.service_teresa_unique_code_id && plan.service.service_teresa_unique_code) {
              title = plan.service.service_teresa_unique_code.display_name
            } else {
              title = plan.service.service_type.display_name
            }
          } else {
            title = plan.service.service_code_id ? plan.service.service_code.display_name : plan.service.service_type.display_name
          }
          return {
            id: plan.id,
            title: title,
            start: utils.formatDay(plan.service.start_day) + 'T' + utils.formatHHMM(plan.service.start_hhmm) + ':00',
            end: utils.formatDay(plan.service.start_day) + 'T' + utils.formatHHMM(plan.service.start_hhmm) + ':01',
            caretime: utils.formatHHMM(plan.service.start_hhmm) + ' ～ ' + utils.formatHHMM(plan.service.end_hhmm),
            staffs: getStaffs(plan),
            instructions: getInstructions(plan),
            tag: plan,
            color: utils.getEventColor(plan),
            allDay: false
          }
        })
        // eslint-disable-next-line no-undef
        $(calendar.value).fullCalendar('removeEvents')
        // eslint-disable-next-line no-undef
        $(calendar.value).fullCalendar('addEventSource', events)
      })
      .finally(() => {
        isProcessing.value = false
      })
  })
    .catch(() => {
    })
}

const getInstructions = (plan) => {
  if (plan.instruction == null) {
    return '未設定'
  } else {
    return plan.instruction.substring(0, 25)
  }
}

const getStaffs = (plan) => {
  if (plan.staff.length > 0) {
    return plan.staff.map(staff => staff.display_name).join('、')
  } else {
    return '未設定'
  }
}

const viewRender = (view, element) => {
  // eslint-disable-next-line no-undef
  load(dayjs(view.currentRange.start, 'YYYY-MM-DD'))
}

const handleEdit = async ($event) => {
  if (isProcessing.value) return

  reloadStop()

  try {
    const planData = $event.plan
    if (!planData) {
      console.error('No plan data found in edit event')
      return
    }

    selectedPlan.value = planData
    showPlanEditModal.value = true
  } catch (error) {
    console.error('Error processing plan data:', error)
    toast.show({
      title: 'エラー',
      body: 'データの処理中にエラーが発生しました。',
      variant: 'danger',
      delay: 2000
    })
  }
}

const monthJump = (month) => {
  $(calendar.value).fullCalendar('gotoDate', month)
  reloadStart()
}

const navLinkDayClick = (date, jsEvent) => {
  if (isProcessing.value || deleteMode.value) return
  if (!hasTarget.value) return
  reloadStop()
  selectedTargetDate.value = dayjs(date).format('YYYY-MM-DD')
  showPlanDayModal.value = true
}

const navLinkWeekClick = (weekStart, jsEvent) => {
  if (isProcessing.value || deleteMode.value) return
  if (!hasTarget.value) return
  reloadStop()
  selectedTargetDate.value = dayjs(weekStart)
  showCopyPlanModal.value = true
}

onMounted(async () => {
  isSakura.value = (Constants.FunctionMask.SAKURA & profile.value.site.function_mask) === Constants.FunctionMask.SAKURA
  await store.dispatch('careplan/init')

  if (selectedOfficeId.value) {
    await store.dispatch('careplan/setTargetOfficeId', selectedOfficeId.value)
  }

  initCalendar()
  reloadTime.value = repos.getAutoReload()
  reloadStart()
})

onBeforeUnmount(() => {
  clearInterval(reloadTimer.value)
})

watch(selectedUserId, async (newVal) => {
  if (newVal !== null) {
    startDate.value = null
    load(selectedTargetDate.value)
  }
}, { immediate: true })
</script>

<style scoped>
/* FullCalendar が DOM を差し込むため :deep が必要（scoped だけだと .fc-event に当たらない） */
#container.delete-mode-overlay :deep(.fc-event.selected) {
  border-left: 5px solid #198754 !important;
  background-image: linear-gradient(
    90deg,
    rgba(25, 135, 84, 0.35) 0%,
    rgba(25, 135, 84, 0.22) 100%
  ) !important;
  box-shadow:
    inset 0 0 0 1px rgba(25, 135, 84, 0.9),
    0 0 0 2px rgba(25, 135, 84, 0.45) !important;
  z-index: 2 !important;
  position: relative !important;
}

#container.delete-mode-overlay :deep(.fc-event.selected .fc-content) {
  font-weight: 700 !important;
  text-shadow: 0 0 2px #fff, 0 0 4px #fff;
}

.custom-checkbox {
  float: left !important;
  z-index: 9999 !important;
  cursor: pointer !important;
  position: relative !important;
  margin-right: 5px !important;
  margin-top: -1px !important;
  pointer-events: auto !important;
}

.select-mode .fc-content {
  display: flex !important;
  align-items: center !important;
  pointer-events: auto !important;
}

.select-mode .fc-time {
  order: 2 !important;
  pointer-events: none !important;
}

.select-mode .fc-title {
  order: 3 !important;
  margin-left: 5px !important;
  pointer-events: none !important;
}

.select-mode .fc-event {
  pointer-events: none !important;
}

.select-mode .custom-checkbox {
  pointer-events: auto !important;
}

.delete-mode-overlay {
  position: relative;
}

/* 削除モード: カレンダー（右カラムの .card 内）以外をグレーアウト。クリックは透過しない（サイド等は操作しない想定） */
#container.delete-mode-overlay .plan-delete-dim-target::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 2;
  pointer-events: none;
  border-radius: 0.25rem;
}

.tooltipevent {
  width: 250px;
  height: 100px;
  padding: 12px;
  background: #aed0ea;
  position: absolute;
  z-index: 10001;
  font-size: 0.8rem;
  box-shadow: 1px 1px 3px 0px #888888;
  line-height: 1rem;
}

.fc-cancelDeleteButton-button {
  display: none;
}

/* カレンダーの日付・週番号をサイトによらず黒で統一（Bootstrap の a:not([href]) の有無に依存しない） */
:deep(.fc-basic-view .fc-day-number),
:deep(.fc-basic-view .fc-week-number) {
  color: #000 !important;
  text-decoration: none;
}
</style>
